import { supabase } from '@common/api/authService';
import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import {
    useCallback, useEffect, useRef, useState
} from 'react';

export type DatabaseEvent = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

export interface SupaSocketConfig {
  table: string;
  schema?: string;
  event?: DatabaseEvent;
  filter?: string;
  enabled?: boolean;
}

export interface SupaSocketCallbacks<T extends Record<string, any> = Record<string, any>> {
  onInsert?: (payload: RealtimePostgresChangesPayload<T>) => void;
  onUpdate?: (payload: RealtimePostgresChangesPayload<T>) => void;
  onDelete?: (payload: RealtimePostgresChangesPayload<T>) => void;
  onChange?: (payload: RealtimePostgresChangesPayload<T>) => void;
  onError?: (error: Error) => void;
}

export interface SupaSocketReturn {
  isConnected: boolean;
  error: Error | null;
  disconnect: () => void;
  reconnect: () => void;
}

/**
 * Custom hook for listening to Supabase realtime changes
 * @param config - Configuration for the realtime subscription
 * @param callbacks - Event callbacks
 * @returns Connection status and control functions
 */
export const useSupaSocket = <T extends Record<string, any> = Record<string, any>>(
    config: SupaSocketConfig,
    callbacks: SupaSocketCallbacks<T> = {}
): SupaSocketReturn => {
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const channelRef = useRef<RealtimeChannel | null>(null);
    const configRef = useRef(config);
    const callbacksRef = useRef(callbacks);

    // Update refs when props change
    useEffect(() => {
        configRef.current = config;
        callbacksRef.current = callbacks;
    }, [config, callbacks]);

    const disconnect = useCallback(() => {
        if (channelRef.current) {
            supabase.removeChannel(channelRef.current);
            channelRef.current = null;
            setIsConnected(false);
            setError(null);
        }
    }, []);

    const connect = useCallback(() => {
        const {
            table, schema = 'public', event = '*', filter, enabled = true 
        } = configRef.current;
    
        if (!enabled || !table) {
            return;
        }

        try {
            // Create channel name
            const channelName = `realtime:${schema}:${table}:${event}${filter ? `:${filter}` : ''}`;
      
            // Create new channel
            const channel = supabase.channel(channelName);
      
            // Configure postgres changes listener
            channel.on(
        'postgres_changes' as any,
        {
            event,
            schema,
            table,
            ...(filter && { filter })
        },
        (payload: RealtimePostgresChangesPayload<T>) => {
            const {
                onInsert, onUpdate, onDelete, onChange 
            } = callbacksRef.current;
          
            // Call specific event callback
            switch (payload.eventType) {
                case 'INSERT':
                    onInsert?.(payload);
                    break;
                case 'UPDATE':
                    onUpdate?.(payload);
                    break;
                case 'DELETE':
                    onDelete?.(payload);
                    break;
            }
          
            // Always call general onChange callback
            onChange?.(payload);
        }
            );

            // Subscribe to channel
            channel.subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    setIsConnected(true);
                    setError(null);
                } else if (status === 'CHANNEL_ERROR') {
                    const error = new Error(`Failed to subscribe to ${table} changes`);
                    setError(error);
                    callbacksRef.current.onError?.(error);
                    setIsConnected(false);
                } else if (status === 'TIMED_OUT') {
                    const error = new Error(`Subscription to ${table} timed out`);
                    setError(error);
                    callbacksRef.current.onError?.(error);
                    setIsConnected(false);
                } else if (status === 'CLOSED') {
                    setIsConnected(false);
                }
            });

            channelRef.current = channel;
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Unknown error occurred');
            setError(error);
            callbacksRef.current.onError?.(error);
        }
    }, []);

    const reconnect = useCallback(() => {
        disconnect();
        setTimeout(connect, 100); // Small delay to ensure cleanup
    }, [disconnect, connect]);

    // Initial connection and cleanup
    useEffect(() => {
        connect();
    
        return () => {
            disconnect();
        };
    }, [connect, disconnect]);

    // Reconnect when config changes
    useEffect(() => {
        // If config changes, specifically enabled status or table details, we should try to connect/reconnect
        // The previous logic only reconnected if ALREADY connected, which fails if it started as disconnected (enabled=false)
        
        if (config.enabled && config.table) {
            // If it's now enabled and has a table, but we aren't connected or the channel is different, we should connect.
            // The simplest way is to just trigger a reconnect/connect cycle.
            // However, since 'connect' is already called on mount, we need to be careful not to double-connect.
            // But 'connect' depends on 'configRef.current' which is updated in a separate useEffect.
            
            // To be safe and ensure we react to 'enabled' flipping from false to true:
            reconnect();
        } else if (!config.enabled) {
            disconnect();
        }
    }, [config.table, config.schema, config.event, config.filter, config.enabled]);

    return {
        isConnected,
        error,
        disconnect,
        reconnect
    };
};

/**
 * Hook for listening to multiple table changes
 * @param configs - Array of table configurations
 * @param callbacks - Event callbacks
 * @returns Connection status and control functions for all subscriptions
 */
export const useMultiSupaSocket = <T extends Record<string, any> = Record<string, any>>(
    configs: SupaSocketConfig[],
    callbacks: SupaSocketCallbacks<T> = {}
): SupaSocketReturn & { connections: Record<string, boolean> } => {
    const [connections, setConnections] = useState<Record<string, boolean>>({});
    const [globalError, setGlobalError] = useState<Error | null>(null);
    const socketsRef = useRef<Record<string, SupaSocketReturn>>({});

    useEffect(() => {
    // Clean up existing sockets
        Object.values(socketsRef.current).forEach(socket => {
            socket.disconnect();
        });
        socketsRef.current = {};

        // Create new sockets for each config
        configs.forEach((config, index) => {
            const key = `${config.table}_${index}`;
      
            // This would need to be implemented differently since we can't call hooks in a loop
            // For now, we'll just track the configs and handle them individually
            setConnections(prev => ({ ...prev, [key]: false }));
        });

        return () => {
            Object.values(socketsRef.current).forEach(socket => {
                socket.disconnect();
            });
        };
    }, [configs]);

    const isConnected = Object.values(connections).some(connected => connected);
    const allConnected = Object.values(connections).every(connected => connected);

    const disconnect = useCallback(() => {
        Object.values(socketsRef.current).forEach(socket => {
            socket.disconnect();
        });
        setConnections({});
    }, []);

    const reconnect = useCallback(() => {
        Object.values(socketsRef.current).forEach(socket => {
            socket.reconnect();
        });
    }, []);

    return {
        isConnected: allConnected,
        error: globalError,
        disconnect,
        reconnect,
        connections
    };
};

export default useSupaSocket;